interface crl8Script {
  createExperience: (id: string) => Promise<void>
  destroyExperience: (id: string) => Promise<void>
}

declare global {
  interface Window {
    crl8?: crl8Script
  }
}

interface Crl8Plugin {
  create: (id: string) => Promise<void> | void
  destroy: (id: string) => Promise<void> | void
}

declare module '#app' {
  interface NuxtApp {
    $crl8: Crl8Plugin
  }
}

declare module 'vue' {
  interface NuxtApp {
    $crl8: Crl8Plugin
  }
}

export default defineNuxtPlugin({
  name: 'crl8',
  setup: () => {
    const { configCrl8SiteName } = useFeatureFlags()
    const route = useRoute()

    if (!configCrl8SiteName) {
      console.info('crl8 is not configured')

      return {
        provide: {
          crl8: {
            create: (_) => _,
            destroy: (_) => _
          }
        }
      }
    }

    /**
     * If crl8SiteNameConfig has an alt key, check if the routes include the current pathname
     * else use the default site id (key)
     * This was done for timberland/timberlandpro since there are two curalate instances for one domain,
     * but it could be set up for other sites with only LD changes.
     */
    const crl8SiteNameValue = computed(() => configCrl8SiteName.alt?.routes?.some((r) => route.path.includes(r))
      ? configCrl8SiteName.alt.key
      : configCrl8SiteName.key)

    let load, status, remove

    watchImmediate(crl8SiteNameValue, async () => {
      if (remove) {
        await remove()
        delete window.crl8
      }

      ({ load, status, remove } = useScript<crl8Script>({
        src: `https://edge.curalate.com/sites/${crl8SiteNameValue.value}/site/latest/site.min.js`,
        key: crl8SiteNameValue.value,
        async: true
      }, {
        trigger: 'manual',
        use() {
          return window.crl8
        }
      }))
    })

    const create = async (id: string) => {
      const crl8 = await load()
      try {
        const oldExperience = await crl8.getExperience(id)
        if (oldExperience)
          await crl8.destroyExperience(id)
      }
      catch {
        // expected if promise is rejected
      }
      await crl8.createExperience(id)
    }

    const destroy = async (id: string) => {
      // If the script is not loaded yet, we don't need to destroy the experience
      if (status.value !== 'loaded') return

      const crl8 = await load()

      await crl8.destroyExperience(id)
    }

    return {
      provide: {
        crl8: {
          create,
          destroy
        }
      }
    }
  }
})
